// apps/client/src/context/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import {
  onAuthStateChanged,
  signOut,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  User
} from 'firebase/auth';
import { auth, functions, db } from '../lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, updateDoc, increment, serverTimestamp } from 'firebase/firestore';
import { posthog } from '../providers/PostHogProvider';
import Loading from '../components/common/Loading';
import SessionTimeoutWarning from '../components/common/SessionTimeoutWarning';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import { classifyAuthError, hashEmail } from '../lib/appCheckRecovery';

// ============================================
// UTILITY: Retry with Exponential Backoff
// ============================================

/**
 * Retry transient failures with exponential backoff (Google SRE pattern).
 * Handles auth token propagation delays between Firebase Auth and Firestore.
 */
const retryWithBackoff = async <T,>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 300
): Promise<T> => {
  let lastError: any;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error: any) {
      lastError = error;

      // Only retry permission errors (indicates auth token not propagated yet)
      const isPermissionError =
        error.code === 'permission-denied' ||
        error.message?.includes('insufficient permissions') ||
        error.message?.includes('Missing or insufficient');

      if (isPermissionError && attempt < maxRetries) {
        const delay = baseDelay * Math.pow(2, attempt);
        console.warn(`[AuthContext] Retry ${attempt + 1}/${maxRetries} after ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        throw error;
      }
    }
  }

  throw lastError;
};

interface CustomClaims {
  companyId?: string;
  role?: string;
  isFounder?: boolean;
  officeIds?: string[];
  canSeeAllOffices?: boolean;
  brokerDocId?: string;
  hideCommissions?: boolean;
  /**
   * Portfolio totals switch: when true, loan-VOLUME figures (loan book, loans
   * settled) stay visible for a user whose commission dollars are hidden. Only
   * meaningful alongside `hideCommissions`; the backend preserves the volume
   * keys for such a caller and zeroes every commission key regardless.
   */
  showLoanTotals?: boolean;
  /**
   * Broker-restricted admin scope: broker doc ids an `office_admin` is limited
   * to. A non-empty list means the account is broker-filtered, and every write
   * control that acts outside that filter is disabled in the UI.
   */
  linkedBrokerDocIds?: string[];
  /**
   * Frontend-only flag set when a founder's session has `?viewAs=<cid>` in
   * the URL. NOT a real Firebase Auth claim — derived in onAuthStateChanged
   * from the live URL + the verified `isFounder` claim. See AuthProvider.
   */
  viewAsActive?: boolean;
  /** Target tenant's companyId when viewAsActive. Frontend-only, derived. */
  viewAsTargetCompanyId?: string;
  [key: string]: any;
}

/**
 * Union of the broker-doc filter as the user doc and the ID token each report
 * it. Deliberately fail-closed: a non-empty list from EITHER source restricts
 * the account, so a stale token can never re-enable a write control the server
 * would refuse, and a not-yet-synced user doc cannot either. Non-list values
 * are treated as restricted-with-no-brokers (the server does the same).
 */
const mergeLinkedBrokerDocIds = (
  userData: Record<string, any>,
  tokenClaims: Record<string, any>
): any => {
  const sources = [userData?.linkedBrokerDocIds, tokenClaims?.linkedBrokerDocIds];
  const present = sources.filter((s) => s !== undefined && s !== null);
  if (present.length === 0) return undefined;
  // A present-but-malformed value is passed through unchanged so the predicate
  // below sees it and restricts, exactly as the server's chokepoint does.
  const malformed = present.find((s) => !Array.isArray(s));
  if (malformed !== undefined) return malformed;
  const merged = new Set<string>();
  present.forEach((s) => (s as unknown[]).forEach((id) => {
    if (typeof id === 'string' && id) merged.add(id);
  }));
  return Array.from(merged);
};

/**
 * Mirrors the server chokepoint (`enforceAuthenticated`'s
 * `allowBrokerFilteredAdmin` default-deny): an `office_admin` whose
 * `linkedBrokerDocIds` is a non-empty list, or is present and not a list, is
 * broker-filtered. Every other role and an absent or empty list is not.
 */
export const isBrokerFilteredAdminClaims = (c: CustomClaims | null): boolean => {
  if (!c || c.role !== 'office_admin') return false;
  const v = (c as any).linkedBrokerDocIds;
  if (v === undefined || v === null) return false;
  if (!Array.isArray(v)) return true;
  return v.length > 0;
};

/**
 * SECURITY (Marketing RBAC deny-by-default): the SPA must not act on stale
 * broker/commission claims if a token is mid-refresh after a demotion to
 * marketing. Mirror the server-side claim-strip (onUserWrite / updateUserRole /
 * invitation flows): for a marketing user, clear broker/commission claims,
 * force canSeeAllOffices + hasAccessRestrictions false, and KEEP hideCommissions
 * true. Mutates and returns the same object for convenience.
 */
const normalizeMarketingClaims = (c: CustomClaims): CustomClaims => {
  if (c.role !== 'marketing' && c.role !== 'service_staff') return c;
  delete c.brokerDocId;
  delete (c as any).brokerId;
  delete (c as any).allowedEntityCodes;
  delete (c as any).linkedBrokerDocIds;
  delete (c as any).linkedLoanBookGrants;
  delete (c as any).visiblePaymentTypes;
  c.canSeeAllOffices = false;
  (c as any).hasAccessRestrictions = false;
  c.hideCommissions = true;
  c.showLoanTotals = false;
  return c;
};

interface AuthContextType {
  user: User | null;
  claims: CustomClaims | null;
  companyId: string | null;
  isFounder: boolean;
  isAuthenticated: boolean;
  loading: boolean;
  brokerDocId: string | null;
  isBroker: boolean;
  isBrokerAssistant: boolean;
  isBrokerFamily: boolean;
  hideCommissions: boolean;
  /**
   * Portfolio totals switch. Loan-VOLUME figures (loan book, loans settled)
   * render when `!hideCommissions || showLoanTotals`; commission dollars are
   * governed by `hideCommissions` alone and this never unhides them.
   */
  showLoanTotals: boolean;
  /**
   * True when the signed-in account is an `office_admin` restricted to a
   * non-empty set of broker docs. Write controls that act outside that filter
   * are disabled rather than hidden, so the account never lands on a
   * server-side `permission-denied` it was invited to hit.
   */
  isBrokerFilteredAdmin: boolean;
  /**
   * True when the active session is a founder view-as session (URL has
   * `?viewAs=<cid>` AND the live ID-token has `isFounder === true`). Components
   * use this to render the ViewAsBanner and to suppress write-action UI.
   */
  viewAsActive: boolean;
  /** Target tenant's companyId when viewAsActive, else null. */
  viewAsTargetCompanyId: string | null;
  login: (email: string, password: string) => Promise<any>;
  signInWithGoogle: () => Promise<any>;
  logout: () => Promise<void>;
  updateUserStatus: (uid: string, status: 'active' | 'revoked') => Promise<any>;
  updateUserRole: (targetUid: string, newRole: string) => Promise<any>;
  refreshClaims: () => Promise<CustomClaims | null>;
  /** Set to true during invitation acceptance to bypass profile check race condition */
  setInvitationAcceptanceMode: (enabled: boolean) => void;
  /** Force re-check of user profile from Firestore (used after invitation acceptance) */
  recheckProfile: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [claims, setClaims] = useState<CustomClaims | null>(null);
  const [loading, setLoading] = useState(true);

  // Ref to track if invitation acceptance is in progress
  // This prevents the race condition where onAuthStateChanged fires before
  // the acceptInvitation cloud function creates the user profile
  const invitationAcceptanceModeRef = useRef<boolean>(false);

  const setInvitationAcceptanceMode = (enabled: boolean) => {
    console.log('[AuthContext] Invitation acceptance mode:', enabled ? 'ENABLED' : 'DISABLED');
    invitationAcceptanceModeRef.current = enabled;
  };

  /**
   * Force re-check of user profile from Firestore.
   * Used after invitation acceptance when the profile has been created server-side.
   */
  const recheckProfile = async (): Promise<void> => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.warn('[AuthContext] recheckProfile called with no current user');
      return;
    }

    console.log('[AuthContext] Rechecking profile for user:', currentUser.uid);

    try {
      // Force token refresh to get latest claims
      const idTokenResult = await currentUser.getIdTokenResult(true);

      // Re-fetch user profile from Firestore
      const userDoc = await retryWithBackoff(
        async () => await getDoc(doc(db, 'users', currentUser.uid))
      );

      if (!userDoc.exists()) {
        console.error('[AuthContext] recheckProfile: Profile still does not exist');
        return;
      }

      const userData = userDoc.data();

      // Validate required fields
      if (!userData.companyId || !userData.role) {
        console.error('[AuthContext] recheckProfile: Profile missing companyId or role');
        return;
      }

      // Check status
      const userStatus = userData.status || 'active';
      if (userStatus === 'revoked' || userStatus === 'suspended') {
        console.error('[AuthContext] recheckProfile: User status is', userStatus);
        await signOut(auth);
        setUser(null);
        setClaims(null);
        return;
      }

      // Build claims from profile
      const nextClaims: CustomClaims = normalizeMarketingClaims({
        ...idTokenResult.claims,
        companyId: userData.companyId,
        role: userData.role,
        isFounder: !!userData.isFounder || !!idTokenResult.claims.isFounder,
        officeIds: userData.assignedOffices || idTokenResult.claims.officeIds || [],
        canSeeAllOffices: userData.canSeeAllOffices || idTokenResult.claims.canSeeAllOffices || false,
        brokerDocId: userData.brokerDocId || idTokenResult.claims.brokerDocId || undefined,
        hideCommissions: !!userData.hideCommissions || !!idTokenResult.claims.hideCommissions,
        showLoanTotals: !!userData.showLoanTotals || !!idTokenResult.claims.showLoanTotals,
        linkedBrokerDocIds: mergeLinkedBrokerDocIds(userData, idTokenResult.claims),
      });

      console.log('[AuthContext] recheckProfile: Profile loaded successfully', {
        companyId: nextClaims.companyId,
        role: nextClaims.role,
      });

      setUser(currentUser);
      setClaims(nextClaims);
    } catch (err: any) {
      console.error('[AuthContext] recheckProfile error:', err);
    }
  };

  // Telemetry wrapper: classify auth failures (esp. App Check rejections) and
  // emit a PostHog event before re-throwing. Firestore writes can't be used
  // here because App Check is enforced on Firestore — PostHog is the only
  // client telemetry path that survives an App Check rejection.
  const captureAuthFailure = (
    err: unknown,
    attemptedEmailHash: string
  ): void => {
    try {
      const ctx = classifyAuthError(err);
      const code =
        typeof err === 'object' && err !== null && typeof (err as { code?: unknown }).code === 'string'
          ? (err as { code: string }).code
          : undefined;
      posthog?.capture('auth.failure', {
        code,
        isAppCheckFailure: ctx.isAppCheckFailure,
        throttleRemainingMs: ctx.throttleRemainingMs,
        isInitialThrottle: ctx.isInitialThrottle,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        cookieEnabled: typeof navigator !== 'undefined' ? navigator.cookieEnabled : false,
        hasIndexedDB: typeof window !== 'undefined' && 'indexedDB' in window,
        attemptedEmailHash,
      });
    } catch {
      // Never throw on telemetry — the login error must surface unchanged.
    }
  };

  const login = async (email: string, password: string) => {
    try {
      return await signInWithEmailAndPassword(auth, email, password);
    } catch (err: unknown) {
      const emailHashValue = await hashEmail(email).catch(() => 'hash_error');
      captureAuthFailure(err, emailHashValue);
      throw err;
    }
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      return await signInWithPopup(auth, provider);
    } catch (err: unknown) {
      captureAuthFailure(err, 'oauth');
      throw err;
    }
  };

  const logout = useCallback(async () => {
    try {
      posthog?.reset();
      await signOut(auth);
    } catch (error: any) {
      // Silently handle logout errors
    }
  }, []);

  const updateUserStatus = async (uid: string, status: 'active' | 'revoked') => {
    if (!uid || !status) throw new Error('uid and status are required');
    if (!['active', 'revoked'].includes(status)) throw new Error('invalid status');

    try {
      const setStatusFn = httpsCallable(functions, 'setUserStatus');
      const res = await setStatusFn({ uid, status });
      return res?.data || { success: true };
    } catch (error: any) {
      throw new Error(error?.message || 'Failed to update user status');
    }
  };

  const updateUserRole = async (targetUid: string, newRole: string) => {
    if (!targetUid || !newRole) throw new Error('targetUid and newRole are required');

    try {
      const updateRoleFn = httpsCallable(functions, 'updateUserRole');
      const res = await updateRoleFn({ targetUid, newRole });
      return res?.data || { success: true };
    } catch (error: any) {
      throw new Error(error?.message || 'Failed to update user role.');
    }
  };

  const refreshClaims = async (): Promise<CustomClaims | null> => {
    if (!auth.currentUser) return null;
    const idTokenResult = await auth.currentUser.getIdTokenResult(true);
    
    if (!idTokenResult.claims.companyId) {
      try {
        const snap = await getDoc(doc(db, 'users', auth.currentUser.uid));
        if (snap.exists()) {
          const u = snap.data();
          const newClaims: CustomClaims = normalizeMarketingClaims({
            ...idTokenResult.claims,
            companyId: u.companyId ?? idTokenResult.claims.companyId,
            role: u.role ?? idTokenResult.claims.role,
            isFounder: !!u.isFounder || !!idTokenResult.claims.isFounder,
          });
          setClaims(newClaims);
          return newClaims;
        }
      } catch (e: any) {
        // Fallback failed, continue with token claims
      }
    }
    // Normalize stale broker/commission claims for marketing (mirror server strip).
    const tokenClaims = normalizeMarketingClaims({ ...idTokenResult.claims } as CustomClaims);
    setClaims(tokenClaims);
    return tokenClaims;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser: any) => {
      try {
        if (!fbUser) {
          // During invitation acceptance, suppress sign-out from race conditions
          // (e.g. Firebase internally fires null between auth state transitions)
          if (invitationAcceptanceModeRef.current) {
            console.log('[AuthContext] Suppressing sign-out during invitation acceptance');
            setLoading(false);
            return;
          }
          setUser(null);
          setClaims(null);
          setLoading(false);
          return;
        }

        // During invitation acceptance, skip all async profile checks
        // AcceptInvitationPage will call recheckProfile() after the server creates the profile
        if (invitationAcceptanceModeRef.current) {
          console.log('[AuthContext] Invitation acceptance in progress — setting user, skipping profile check');
          setUser(fbUser);
          setClaims(null);
          setLoading(false);
          return;
        }

        // CRITICAL: Force token refresh BEFORE Firestore operations
        // This ensures expired cached tokens are refreshed, preventing permission errors
        const idTokenResult = await fbUser.getIdTokenResult(true);

        // SECURITY: Validate user has a valid profile in Firestore
        // Use retry logic to handle auth token propagation delays
        const userDoc = await retryWithBackoff(
          async () => await getDoc(doc(db, 'users', fbUser.uid))
        );

        if (!userDoc.exists()) {
          // SECURITY: User authenticated with Firebase but has NO profile in Firestore
          // This should not happen with the atomic invitation acceptance flow
          console.error('[AuthContext] SECURITY: User has no profile in Firestore, signing out');
          await signOut(auth);
          setUser(null);
          setClaims(null);
          setLoading(false);
          return;
        }

        const userData = userDoc.data();

        // SECURITY: Check if user has required fields
        if (!userData.companyId || !userData.role) {
          console.error('[AuthContext] SECURITY: User profile missing companyId or role, signing out');
          await signOut(auth);
          setUser(null);
          setClaims(null);
          setLoading(false);
          return;
        }

        // SECURITY: Check if user status is active
        // Default to 'active' for backwards compatibility with existing users
        const userStatus = userData.status || 'active';
        if (userStatus === 'revoked' || userStatus === 'suspended') {
          console.error('[AuthContext] SECURITY: User status is revoked/suspended:', userStatus);
          await signOut(auth);
          setUser(null);
          setClaims(null);
          setLoading(false);
          return;
        }

        // Build custom claims from Firestore data and ID token.
        // normalizeMarketingClaims strips stale broker/commission claims for a
        // marketing user (mirror of the server-side claim-strip). The founder
        // view-as branch below runs AFTER this and forces role=super_admin, so
        // it is unaffected (view-as is founder-only, never a marketing user).
        const nextClaims: CustomClaims = normalizeMarketingClaims({
          ...idTokenResult.claims,
          companyId: userData.companyId,
          role: userData.role,
          isFounder: !!userData.isFounder || !!idTokenResult.claims.isFounder,
          officeIds: userData.assignedOffices || idTokenResult.claims.officeIds || [],
          canSeeAllOffices: userData.canSeeAllOffices || idTokenResult.claims.canSeeAllOffices || false,
          brokerDocId: userData.brokerDocId || idTokenResult.claims.brokerDocId || undefined,
          hideCommissions: !!userData.hideCommissions || !!idTokenResult.claims.hideCommissions,
          showLoanTotals: !!userData.showLoanTotals || !!idTokenResult.claims.showLoanTotals,
          linkedBrokerDocIds: mergeLinkedBrokerDocIds(userData, idTokenResult.claims),
        });

        // ============================================
        // FOUNDER VIEW-AS BRANCH (Cut 2 PR-B)
        // ============================================
        // When the URL has `?viewAs=<companyId>` AND the live ID-token claim
        // `isFounder === true`, override `companyId` to the target tenant so all
        // downstream hooks (useMonthlyAggregates, useRolling3RunWindow, etc.)
        // query that tenant's data. The backend's existing founder bypass in
        // `enforceCompanyAccess` permits the cross-tenant reads and audits each
        // one to `admin_audit_logs` via `ADMIN_COMPANY_VIEWED`. Read-only
        // safety is enforced at the WRITE boundary by `blockFounderClientWrite`
        // — no frontend gate is depended on for security.
        //
        // The `isFounder` source-of-truth here is the verified ID-token claim
        // (NOT the merged `nextClaims.isFounder` which OR's in userData) — only
        // an Auth-issued claim can grant cross-tenant view-as. Param validated
        // against /^[a-zA-Z0-9_-]{6,64}$/ to refuse arbitrary string injection.
        //
        // Non-founders hitting `?viewAs=<...>` are silently no-op'd: no error,
        // no UI signal, no log noise. The param is treated as if absent.
        const viewAsParam =
          typeof window !== 'undefined'
            ? new URLSearchParams(window.location.search).get('viewAs')
            : null;
        if (
          viewAsParam &&
          idTokenResult.claims.isFounder === true &&
          /^[a-zA-Z0-9_-]{6,64}$/.test(viewAsParam)
        ) {
          nextClaims.companyId = viewAsParam;
          // Override role to 'super_admin' so the founder passes <RequireRole>
          // route guards on the client app's tenant-scoped pages (Reports,
          // Settings, Team Management, Office dashboards, etc.). The backend
          // does NOT trust this client-side role — every callable inspects
          // request.auth.token.role + isFounder directly. The override is
          // purely a navigation-affordance change so the founder can reach
          // the same pages a super_admin of the target tenant could reach.
          // Without this, role='founder' fails every RequireRole(['super_admin', ...])
          // check in apps/client/src/routes/AppRoutes.tsx and bounces back to /.
          nextClaims.role = 'super_admin';
          nextClaims.canSeeAllOffices = true;
          nextClaims.officeIds = [];
          nextClaims.brokerDocId = undefined;
          nextClaims.hideCommissions = false;
          nextClaims.showLoanTotals = false;
          nextClaims.linkedBrokerDocIds = undefined;
          nextClaims.viewAsActive = true;
          nextClaims.viewAsTargetCompanyId = viewAsParam;
          console.info(
            '[AuthContext] Founder view-as active — companyId overridden to',
            viewAsParam,
            '(role overridden to super_admin for client-app nav guards)'
          );
        }

        // PostHog user identification (non-blocking)
        try {
          posthog?.identify(fbUser.uid, {
            companyId: nextClaims.companyId || '',
            role: nextClaims.role || '',
          });
        } catch {} // Never block auth on analytics

        // Update lastLogin (fire-and-forget, non-blocking)
        updateDoc(doc(db, 'users', fbUser.uid), {
          lastLogin: serverTimestamp(),
          loginCount: increment(1),
        }).catch(() => {});

        setUser(fbUser);
        setClaims(nextClaims);
      } catch (err: any) {
        console.error('[AuthContext] Error in auth state change:', err);
        // During invitation acceptance, don't sign out on errors —
        // AcceptInvitationPage handles its own error recovery
        if (!invitationAcceptanceModeRef.current) {
          await signOut(auth).catch(() => {});
          setUser(null);
          setClaims(null);
        }
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Session timeout: 4 hours idle → auto-logout, 5 min warning
  const isAuthenticated = !!user && !!claims && !!claims.companyId;

  const handleSessionTimeout = useCallback(async () => {
    try {
      posthog?.reset();
      await signOut(auth);
    } catch {}
    window.location.href = '/login?expired=true';
  }, []);

  const { showWarning, remainingSeconds, extendSession } = useSessionTimeout({
    timeoutMs: 4 * 60 * 60 * 1000,  // 4 hours
    warningMs: 5 * 60 * 1000,        // 5 min warning
    onTimeout: handleSessionTimeout,
    enabled: isAuthenticated,
  });

  if (loading) {
    return <Loading />;
  }

  const value: AuthContextType = {
    user,
    claims,
    companyId: claims?.companyId ?? null,
    isFounder: !!claims?.isFounder,
    isAuthenticated,
    loading,
    brokerDocId: claims?.brokerDocId ?? null,
    isBroker: claims?.role === 'broker',
    isBrokerAssistant: claims?.role === 'broker_assistant',
    isBrokerFamily: claims?.role === 'broker' || claims?.role === 'broker_assistant' || claims?.role === 'credit_analyst',
    hideCommissions: !!claims?.hideCommissions,
    showLoanTotals: !!claims?.showLoanTotals,
    isBrokerFilteredAdmin: isBrokerFilteredAdminClaims(claims),
    viewAsActive: !!claims?.viewAsActive,
    viewAsTargetCompanyId: claims?.viewAsTargetCompanyId ?? null,
    login,
    signInWithGoogle,
    logout,
    updateUserStatus,
    updateUserRole,
    refreshClaims,
    setInvitationAcceptanceMode,
    recheckProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {showWarning && (
        <SessionTimeoutWarning
          remainingSeconds={remainingSeconds}
          onExtend={extendSession}
          onSignOut={logout}
        />
      )}
    </AuthContext.Provider>
  );
};